// Accessible modal surfaces: focus moves in on open, Tab stays inside,
// Escape closes, and focus returns to whatever opened it.
import { useEffect, useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { w } from "../i18n";

const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex='-1'])";

function useFocusTrap(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    (node?.querySelector<HTMLElement>("[data-autofocus]") || node?.querySelector<HTMLElement>(FOCUSABLE) || node)?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.stopPropagation(); onClose(); return; }
      if (event.key !== "Tab" || !node) return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(item => item.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("keydown", onKey, true); opener?.focus?.(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
}

export function Dialog({ title, onClose, children, footer, size = "md" }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; size?: "sm" | "md";
}) {
  const ref = useFocusTrap(onClose);
  const titleId = useId();
  return <>
    <div className="ws-overlay" onClick={onClose} aria-hidden="true" />
    <div ref={ref} className={`ws-dialog ${size === "sm" ? "sm" : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <div className="ws-panel-head"><h2 id={titleId}>{title}</h2>
        <button className="ws-icon-btn" onClick={onClose} aria-label={w("common.close")}><X size={16} /></button></div>
      <div className="ws-dialog-body">{children}</div>
      {footer && <div className="ws-dialog-foot">{footer}</div>}
    </div>
  </>;
}

export function SidePanel({ title, subtitle, onClose, children, wide = false }: {
  title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean;
}) {
  const ref = useFocusTrap(onClose);
  const titleId = useId();
  return <>
    <div className="ws-overlay" onClick={onClose} aria-hidden="true" />
    <aside ref={ref} className={`ws-panel ${wide ? "wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <div className="ws-panel-head"><div><h2 id={titleId}>{title}</h2>{subtitle && <div className="ws-small ws-muted">{subtitle}</div>}</div>
        <button className="ws-icon-btn" onClick={onClose} aria-label={w("common.close")}><X size={16} /></button></div>
      <div className="ws-panel-body">{children}</div>
    </aside>
  </>;
}
